import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { formatEurCompact } from "@/lib/formatters";
import { updateProject } from "@/api/projects";
import { useProjectStore } from "@/stores/project.store";
import { usePermissions } from "@/hooks/usePermissions";
import type { Project } from "@/api/types";

/** Mirrors `project.config.group_filters` from the M&A engine config. */
interface GroupFilters {
  exclude_foreign_subsidiaries?: boolean;
  exclude_pure_holdings?: boolean;
  operational_holding_min_employees?: number | null;
}

/** Mirrors `project.config.acquisition_filters`. */
interface AcquisitionFilters {
  exclude_non_acquirable?: boolean;
  exclude_high_risk?: boolean;
  max_employees_threshold?: number | null;
  max_capital_threshold?: number | null;
}

/** Mirrors `project.config.segmentation` — A/B/C notation thresholds /18. */
interface Segmentation {
  A?: number | null;
  B?: number | null;
  C?: number | null;
}

interface Draft {
  group: GroupFilters;
  acquisition: AcquisitionFilters;
  segmentation: Segmentation;
}

interface FiltersSectionProps {
  project: Project;
}

export function FiltersSection({ project }: FiltersSectionProps) {
  const canEdit = usePermissions().canEditScoring;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  const initial = useMemo<Draft>(
    () => ({
      group: normalizeGroup(project.config?.group_filters),
      acquisition: normalizeAcquisition(project.config?.acquisition_filters),
      segmentation: normalizeSegmentation(project.config?.segmentation),
    }),
    [project.config],
  );

  const [draft, setDraft] = useState<Draft>(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const isDirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(initial),
    [draft, initial],
  );

  function patchGroup<K extends keyof GroupFilters>(
    key: K,
    value: GroupFilters[K],
  ) {
    setDraft((d) => ({ ...d, group: { ...d.group, [key]: value } }));
  }

  function patchAcquisition<K extends keyof AcquisitionFilters>(
    key: K,
    value: AcquisitionFilters[K],
  ) {
    setDraft((d) => ({
      ...d,
      acquisition: { ...d.acquisition, [key]: value },
    }));
  }

  function patchSegmentation<K extends keyof Segmentation>(
    key: K,
    value: Segmentation[K],
  ) {
    setDraft((d) => ({
      ...d,
      segmentation: { ...d.segmentation, [key]: value },
    }));
  }

  async function save() {
    setSubmitting(true);
    try {
      const updated = await updateProject(project.slug, {
        config: {
          group_filters: draft.group,
          acquisition_filters: draft.acquisition,
          segmentation: draft.segmentation,
        },
      });
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      toast.success("Filters saved.");
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setDraft(initial);
  }

  return (
    <section className="space-y-5">
      <header>
        <h2 className="text-lg font-medium tracking-tight text-slate-900">
          Filters
        </h2>
        <p className="text-sm text-slate-500">
          Group, acquisition and segmentation rules applied during scoring.
        </p>
      </header>

      <fieldset
        disabled={!canEdit}
        className="space-y-5 disabled:opacity-60"
      >
        <Card title="Group filters">
          <ToggleRow
            label="Exclude foreign subsidiaries"
            checked={!!draft.group.exclude_foreign_subsidiaries}
            onChange={(v) => patchGroup("exclude_foreign_subsidiaries", v)}
          />
          <ToggleRow
            label="Exclude pure holdings"
            checked={!!draft.group.exclude_pure_holdings}
            onChange={(v) => patchGroup("exclude_pure_holdings", v)}
          />
          <SliderRow
            label="Operational holding min employees"
            min={0}
            max={500}
            step={5}
            value={draft.group.operational_holding_min_employees ?? 0}
            display={String(
              draft.group.operational_holding_min_employees ?? 0,
            )}
            onChange={(n) =>
              patchGroup("operational_holding_min_employees", n)
            }
          />
        </Card>

        <Card title="Acquisition filters">
          <ToggleRow
            label="Exclude non-acquirable"
            checked={!!draft.acquisition.exclude_non_acquirable}
            onChange={(v) =>
              patchAcquisition("exclude_non_acquirable", v)
            }
          />
          <ToggleRow
            label="Exclude high risk"
            checked={!!draft.acquisition.exclude_high_risk}
            onChange={(v) => patchAcquisition("exclude_high_risk", v)}
          />
          <SliderRow
            label="Max employees"
            min={0}
            max={20_000}
            step={100}
            value={draft.acquisition.max_employees_threshold ?? 0}
            display={(
              draft.acquisition.max_employees_threshold ?? 0
            ).toLocaleString()}
            onChange={(n) =>
              patchAcquisition("max_employees_threshold", n)
            }
          />
          <SliderRow
            label="Max capital"
            min={0}
            max={500_000_000}
            step={1_000_000}
            value={draft.acquisition.max_capital_threshold ?? 0}
            display={formatEurCompact(
              draft.acquisition.max_capital_threshold ?? 0,
            )}
            onChange={(n) => patchAcquisition("max_capital_threshold", n)}
          />
        </Card>

        <Card title="Segmentation">
          <p className="-mt-2 mb-1 text-xs text-slate-500">
            Minimum notation (out of 18) to land in each segment.
          </p>
          <SliderRow
            label="Segment A ≥ notation"
            min={0}
            max={18}
            step={1}
            value={draft.segmentation.A ?? 0}
            display={`${draft.segmentation.A ?? 0} / 18`}
            onChange={(n) => patchSegmentation("A", n)}
          />
          <SliderRow
            label="Segment B ≥ notation"
            min={0}
            max={18}
            step={1}
            value={draft.segmentation.B ?? 0}
            display={`${draft.segmentation.B ?? 0} / 18`}
            onChange={(n) => patchSegmentation("B", n)}
          />
          <SliderRow
            label="Segment C ≥ notation"
            min={0}
            max={18}
            step={1}
            value={draft.segmentation.C ?? 0}
            display={`${draft.segmentation.C ?? 0} / 18`}
            onChange={(n) => patchSegmentation("C", n)}
          />
        </Card>
      </fieldset>

      {canEdit && (
        <div className="flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="ghost"
            onClick={reset}
            disabled={!isDirty || submitting}
          >
            Reset
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={save}
            disabled={!isDirty || submitting}
          >
            {submitting && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            Save changes
          </Button>
        </div>
      )}
    </section>
  );
}

// ─── Layout primitives ───────────────────────────────────────────────────

function Card({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-plane bg-white p-6 shadow-plane">
      <h3 className="mb-4 text-sm font-medium tracking-tight text-slate-900">
        {title}
      </h3>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

interface SliderRowProps {
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  display: string;
  onChange: (n: number) => void;
}

function SliderRow({
  label,
  min,
  max,
  step,
  value,
  display,
  onChange,
}: SliderRowProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="font-mono text-xs text-slate-500">{display}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(arr) => onChange(arr[0])}
      />
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function normalizeGroup(raw: unknown): GroupFilters {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  return {
    exclude_foreign_subsidiaries:
      typeof r.exclude_foreign_subsidiaries === "boolean"
        ? r.exclude_foreign_subsidiaries
        : false,
    exclude_pure_holdings:
      typeof r.exclude_pure_holdings === "boolean"
        ? r.exclude_pure_holdings
        : false,
    operational_holding_min_employees:
      typeof r.operational_holding_min_employees === "number"
        ? r.operational_holding_min_employees
        : null,
  };
}

function normalizeAcquisition(raw: unknown): AcquisitionFilters {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  return {
    exclude_non_acquirable:
      typeof r.exclude_non_acquirable === "boolean"
        ? r.exclude_non_acquirable
        : false,
    exclude_high_risk:
      typeof r.exclude_high_risk === "boolean" ? r.exclude_high_risk : false,
    max_employees_threshold:
      typeof r.max_employees_threshold === "number"
        ? r.max_employees_threshold
        : null,
    max_capital_threshold:
      typeof r.max_capital_threshold === "number"
        ? r.max_capital_threshold
        : null,
  };
}

function normalizeSegmentation(raw: unknown): Segmentation {
  if (!raw || typeof raw !== "object") return {};
  const r = raw as Record<string, unknown>;
  const num = (v: unknown): number | null =>
    typeof v === "number" && Number.isFinite(v) ? v : null;
  return { A: num(r.A), B: num(r.B), C: num(r.C) };
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save filters.";
}
